import React from "react";
import { Box, CircularProgress, Collapse, IconButton, Tooltip, Typography } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useTheme } from "@mui/material/styles";

const AIEvidenceTableContainer = ({
  title,
  tableExpanded,
  onToggleExpand,
  loading,
  children,
}) => {
  const theme = useTheme();
  const tc = theme.customColors;

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "16px",
        overflow: "hidden",
        backgroundColor: tc.background.main,
        boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)",
        transition: "box-shadow 0.3s ease",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
        },
        "@keyframes aiGradientFlow": {
          "0%": { backgroundPosition: "0% 50%" },
          "50%": { backgroundPosition: "100% 50%" },
          "100%": { backgroundPosition: "0% 50%" },
        },
        "@keyframes aiSparkle": {
          "0%, 100%": { transform: "scale(1) rotate(0deg)", filter: "drop-shadow(0 0 2px rgba(59,130,246,0.3))" },
          "50%": { transform: "scale(1.12) rotate(6deg)", filter: "drop-shadow(0 0 6px rgba(59,130,246,0.5))" },
        },
        "@keyframes aiFadeSlideIn": {
          from: { opacity: 0, transform: "translateY(6px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
        "@keyframes aiShimmer": {
          "0%": { backgroundPosition: "200% 0" },
          "100%": { backgroundPosition: "-200% 0" },
        },
      }}
    >
      {/* Header */}
      <Box
        onClick={onToggleExpand}
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 1.5,
          cursor: "pointer",
          background: `linear-gradient(135deg, rgba(59,130,246,0.09) 0%, rgba(37,99,235,0.06) 40%, rgba(59,130,246,0.03) 100%)`,
          "&::after": tableExpanded ? {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: "5%",
            right: "5%",
            height: "1px",
            background: "linear-gradient(90deg, transparent, rgba(59,130,246,0.15), transparent)",
          } : {},
          "&:hover": {
            background: `linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(37,99,235,0.06) 40%, rgba(59,130,246,0.03) 100%)`,
          },
          transition: "background 0.2s ease",
        }}
      >
        <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 1.25, position: "relative", zIndex: 1 }}>
          <AutoAwesomeIcon
            sx={{
              fontSize: 20,
              color: "#3B82F6",
              animation: "aiSparkle 3s ease-in-out infinite",
            }}
          />
          {title}
          <Tooltip title="How Rego AI works" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                window.open("/admin/regoai", "_blank", "noopener");
              }}
              sx={{
                p: 0.375,
                color: tc.text.disabled,
                flexShrink: 0,
                transition: "transform 160ms ease, color 160ms ease",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "#3B82F6",
                  transform: "translate(2px, -2px)",
                },
                "@media (prefers-reduced-motion: reduce)": {
                  transition: "color 160ms ease",
                  "&:hover": { transform: "none" },
                },
              }}
            >
              <ArrowOutwardIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <IconButton
          size="small"
          sx={{
            position: "relative",
            zIndex: 1,
            color: tc.text.secondary,
            transition: "all 180ms ease",
            "&:hover": { backgroundColor: tc.background.hover, color: tc.text.strong },
          }}
        >
          {tableExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={tableExpanded}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              py: 5,
              gap: 1.5,
            }}
          >
            {/* AI-style loading dots */}
            <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #3B82F6, #1E40AF)",
                    animation: "aiDotPulse 1.4s ease-in-out infinite",
                    animationDelay: `${i * 0.16}s`,
                    "@keyframes aiDotPulse": {
                      "0%, 80%, 100%": { transform: "scale(0.6)", opacity: 0.4 },
                      "40%": { transform: "scale(1)", opacity: 1 },
                    },
                  }}
                />
              ))}
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: tc.text.secondary,
                fontWeight: 500,
                fontSize: "0.84rem",
                // Shimmer text effect
                background: `linear-gradient(90deg, ${tc.text.secondary} 25%, ${tc.status.info.main} 50%, ${tc.text.secondary} 75%)`,
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                animation: "aiShimmer 2.5s linear infinite",
              }}
            >
              AI is analyzing evidence...
            </Typography>
          </Box>
        ) : (
          children
        )}
      </Collapse>
    </Box>
  );
};

export default AIEvidenceTableContainer;
